import type { B2bOrderInfo } from "./b2b.js";
import type { FbaOrderInfo } from "./fba.js";
import type { Money } from "./product.js";

/**
 * Kanonischer Fulfillment-Auftrag. Entspricht einer Shopify-FulfillmentOrder
 * (nicht der gesamten Shopify-Order): ein Auftrag = eine Lieferung aus einem Lager.
 */

export type OrderStatus =
  | "received" // vom Kanal empfangen
  | "on_hold" // durch Regel oder manuell gesperrt
  | "ready" // Regeln bestanden, wartet auf Übergabe
  | "submitted" // an Fulfillment-Backend übergeben ("Übertragen"), noch nicht angefasst – stornierbar
  | "in_progress" // im Lager in Bearbeitung (Kommissionierung) – Storno nicht mehr möglich
  | "partially_shipped"
  | "shipped"
  | "cancelled"
  | "failed";

/** Optimierungsstufen von Logiful – als FFN-Attribut "optimization" am Ausgang, z. B. zum A/B-Auswerten */
export const OPTIMIZATION_LEVELS = ["level-shippingmethod", "level-address", "level-packstation"] as const;
export type OptimizationLevel = (typeof OPTIMIZATION_LEVELS)[number];
export const OPTIMIZATION_ATTRIBUTE = "optimization";

/** Gründe für eine Nachlieferung */
export const RESHIP_REASONS = {
  not_delivered: "Nicht zugestellt / Rücklauf",
  lost: "Sendung verloren",
  damaged: "Beschädigt angekommen",
  missing_item: "Position fehlte",
  wrong_item: "Falscher Artikel",
  other: "Sonstiges",
} as const;
export type ReshipReason = keyof typeof RESHIP_REASONS;

export interface Address {
  company?: string;
  firstName?: string;
  lastName: string;
  street: string;
  houseNumber?: string;
  addition?: string;
  zip: string;
  city: string;
  state?: string;
  /** ISO-3166-1 alpha-2 */
  country: string;
  email?: string;
  phone?: string;
}

export interface OrderLine {
  id: string;
  productId?: string;
  sku: string;
  /** Artikel im Kanal (Shopify-Varianten-GID) – zuverlässiger als die SKU, die Kunden ändern oder vergessen */
  channelVariantId?: string;
  name: string;
  quantity: number;
  unitPrice?: Money;
  /** vorgegebene Chargen (Odoo-Lieferschein, B2B) – gehen als Notiz an die Lager-Position */
  batches?: LineBatch[];
}

/** vorgegebene Charge mit MHD (YYYY-MM-DD) und Menge */
export interface LineBatch { batch: string; bestBefore?: string | null; quantity: number }

const dmy = (ymd: string) => (/^\d{4}-\d{2}-\d{2}/.test(ymd) ? `${ymd.slice(8, 10)}.${ymd.slice(5, 7)}.${ymd.slice(0, 4)}` : ymd);

/** Präfix für zurückgehaltene Aufträge, deren vorgegebene Charge nicht (ausreichend) im Lager liegt */
export const BATCH_HOLD_PREFIX = "Vorgegebene Charge: ";

/** Notiz an die Lager-Position, z. B. „Charge L2450 MHD 31.08.2027 (10 Stk); Charge L2451 MHD 30.09.2027 (5 Stk)“ */
export function batchNote(batches: LineBatch[] | undefined): string | null {
  const list = (batches ?? []).filter((b) => b.batch && b.quantity > 0);
  if (!list.length) return null;
  return list.map((b) => `Charge ${b.batch}${b.bestBefore ? ` MHD ${dmy(b.bestBefore)}` : ""} (${b.quantity} Stk)`).join("; ");
}

/**
 * Vorgegebene Chargen gegen den Chargenbestand im Lager prüfen. Nur Artikel mit bekanntem Chargenbestand werden
 * geprüft (ohne Chargendaten keine Aussage möglich). Ergebnis: Sperrgründe je fehlender Charge.
 */
export function batchIssues(lines: { sku: string; productId?: string | null; batches?: LineBatch[] }[], lots: { productId: string; batch: string | null; available: number }[]): string[] {
  const need = new Map<string, { sku: string; productId: string; batch: string; quantity: number }>();
  for (const l of lines) {
    if (!l.productId) continue;
    for (const b of l.batches ?? []) {
      if (!b.batch || b.quantity <= 0) continue;
      const k = `${l.productId}|${b.batch}`;
      const cur = need.get(k);
      if (cur) cur.quantity += b.quantity;
      else need.set(k, { sku: l.sku, productId: l.productId, batch: b.batch, quantity: b.quantity });
    }
  }
  const out: string[] = [];
  for (const n of need.values()) {
    const forProduct = lots.filter((x) => x.productId === n.productId);
    if (!forProduct.length) continue;
    const free = forProduct.filter((x) => x.batch === n.batch).reduce((s, x) => s + x.available, 0);
    if (free < n.quantity) out.push(`${BATCH_HOLD_PREFIX}${n.sku} ${n.batch} (benötigt ${n.quantity}, im Lager ${Math.max(0, free)})`);
  }
  return out;
}

export interface Order {
  id: string;
  tenantId: string;
  channelId: string;
  /** Lesbare Auftragsnummer aus dem Shop, z. B. "#1042" */
  orderNumber: string;
  status: OrderStatus;
  placedAt: string;
  financialStatus: "paid" | "pending" | "authorized" | "partially_paid" | "refunded" | "voided" | "unknown";
  shippingAddress: Address;
  /** Versandart-Titel aus dem Shop (für Mapping) */
  channelShippingMethod?: string;
  /** Gemappte Versandart des Fulfillment-Backends */
  shippingMethodId?: string;
  tags: string[];
  note?: string;
  priority: number;
  lines: OrderLine[];
  total?: Money;
  holdReasons: string[];
  /** Lager, das diesen Auftrag versendet (bei mehreren Lagern je Kunde); leer = Hauptlager */
  warehouseId?: string;
  /** Übergabe im Lager-System: fulfiller = direkt an Logiful (Standard), merchant = nur angelegt (FFN „Vorbereitung“) */
  handover?: "fulfiller" | "merchant";
  /** trotz Fehlbestand anlegen (FFN: oversale) – Logiful versendet nach Wareneingang */
  oversale?: boolean;
  /** freie Zusatzfelder fürs Lager (FFN: attributes am Ausgang) */
  attributes?: { key: string; value: string }[];
  /** B2B-Lieferung an einen Händler (Liefertermin, Bestellnummer, Vorgaben) */
  b2b?: B2bOrderInfo;
  /** Pre-FBA-Sendung an ein Amazon-Lager; labelsComplete = alle Amazon-Etiketten liegen vor */
  fba?: FbaOrderInfo & { labelsComplete: boolean };
}

export interface ShipmentLine {
  sku: string;
  quantity: number;
  /** Position im Kanal (Shopify: FulfillmentOrder-Position) – wenn bekannt, gilt sie vor der SKU */
  externalLineId?: string | null;
  batch?: string | null;
  /** YYYY-MM-DD */
  bestBefore?: string | null;
}
export interface ShipmentPackage {
  trackingNumber: string | null;
  /** kg */
  weight: number | null;
  /** cm (L × B × H) */
  dimensions: { length: number; width: number; height: number } | null;
}

export interface Shipment {
  orderId: string;
  carrier: string;
  trackingNumbers: string[];
  trackingUrls: string[];
  shippedAt: string;
  /** Inhalt der Versandmeldung (Charge/MHD, soweit das Lager sie meldet) */
  lines: ShipmentLine[];
  /** Pakete der Versandmeldung – das Lager meldet den Inhalt je Meldung, nicht je Paket */
  packages?: ShipmentPackage[];
  /** Versandart des Lagers (für hinterlegte Tracking-Links je Versandart) */
  shippingMethodId?: string | null;
  /** Dienstleistername ist von Logiful festgelegt → unverändert an den Shop (keine automatische Zuordnung) */
  carrierExact?: boolean;
}

/**
 * Versandmeldung je Artikel zusammengefasst: Das Lager meldet dieselbe SKU oft in mehreren Positionen (Charge, MHD,
 * Lagerplatz – z. B. 1 + 2 + 3 Stück). Für Meldungen an Shops immer summieren, nie je SKU überschreiben.
 */
export function sumShipmentLines<T extends { sku: string; quantity: number; externalLineId?: string | null }>(lines: T[]): { sku: string; quantity: number; externalLineId?: string | null }[] {
  const by = new Map<string, { sku: string; quantity: number; externalLineId?: string | null }>();
  for (const l of lines) {
    const cur = by.get(l.sku);
    if (cur) cur.quantity += Number(l.quantity) || 0;
    else by.set(l.sku, { sku: l.sku, quantity: Number(l.quantity) || 0, ...(l.externalLineId !== undefined ? { externalLineId: l.externalLineId } : {}) });
  }
  return [...by.values()];
}

export interface StockLevel {
  productId: string;
  sku: string;
  warehouseRef: string;
  /** physisch vorhanden */
  onHand: number;
  /** verfügbar für neue Aufträge */
  available: number;
  /** für offene Aufträge reserviert */
  reserved: number;
  /** vom Lager gesperrt (z. B. beschädigt, Qualitätsprüfung) */
  blocked?: number;
  /** angekündigt durch Wareneingänge */
  announced?: number;
  updatedAt: string;
}

/** Vollständiger Bestand eines Artikels in einem Lager (Abgleich ohne Änderungsfeed) */
export interface StockSnapshotItem {
  productRef: string;
  warehouseRef: string;
  onHand: number;
  available: number;
  reserved: number;
  blocked: number;
  announced: number;
}

/** Wareneingang (Avis): Kunde meldet eine Lieferung ans Lager an */
export type InboundStatus = "draft" | "announced" | "partially_received" | "received" | "closed" | "cancelled";

export interface InboundLine {
  id: string;
  productId: string;
  sku: string;
  name: string;
  quantityExpected: number;
  quantityReceived: number;
  /** angekündigte Charge */
  batch?: string;
  /** angekündigtes MHD (YYYY-MM-DD) */
  bestBefore?: string;
}

/** Positions-Anmerkung fürs Lager, z. B. „CHARGE: L2409 | MHD: 01.01.2027“ – leer ohne Angaben */
export function inboundLineNote(l: { batch?: string | null; bestBefore?: string | null }): string {
  const parts: string[] = [];
  if (l.batch?.trim()) parts.push(`CHARGE: ${l.batch.trim()}`);
  if (l.bestBefore) {
    const [y, m, d] = l.bestBefore.slice(0, 10).split("-");
    parts.push(`MHD: ${d}.${m}.${y}`);
  }
  return parts.join(" | ");
}

export interface Inbound {
  id: string;
  tenantId: string;
  inboundNumber: string;
  /** Ziel-Lager; leer = Hauptlager */
  warehouseId?: string;
  status: InboundStatus;
  expectedAt?: string;
  supplier?: string;
  purchaseOrderNumber?: string;
  note?: string;
  lines: InboundLine[];
}

export interface InboundUpdate {
  inboundRef: string;
  status: InboundStatus;
  /** je externer Artikel-ID: eingegangene Menge */
  received: { productRef: string; quantityReceived: number; /** unsere Positions-ID (FFN inboundItemId) – eindeutig auch bei mehreren Chargen je Artikel */ lineRef?: string }[];
}

/** Bestand je Charge/MHD in einem Lager */
export interface StockLot {
  productRef: string;
  sku: string;
  warehouseRef: string;
  batch: string | null;
  /** YYYY-MM-DD oder null, wenn nicht MHD-geführt */
  bestBefore: string | null;
  quantity: number;
  blocked: number;
}
